import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/services/apiClient';
import type { Session, DisconnectRequest, LogOffRequest } from '@/types/api';
import { SessionState } from '@/types/api';

const QUERY_KEYS = {
  sessions: ['sessions'] as const,
  session: (id: string) => ['session', id] as const,
  sessionStatus: (id: string) => ['session', id, 'status'] as const,
};

export function useSessions() {
  return useQuery({
    queryKey: QUERY_KEYS.sessions,
    queryFn: () => apiClient.getSessions(),
    refetchInterval: 30000, // Refresh every 30 seconds
    staleTime: 10000, // Consider data stale after 10 seconds
  });
}

export function useRefreshSessions() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: () => apiClient.refreshSessions(),
    onSuccess: (sessions) => {
      // Update the sessions cache with fresh data
      queryClient.setQueryData(QUERY_KEYS.sessions, sessions);
      
      // Invalidate individual session queries to ensure they refetch
      queryClient.invalidateQueries({ 
        queryKey: ['session'], 
        exact: false 
      });
    },
  });
}

export function useSession(sessionId: string) {
  return useQuery({
    queryKey: QUERY_KEYS.session(sessionId),
    queryFn: () => apiClient.getSession(sessionId),
    enabled: !!sessionId,
  });
}

export function useSessionStatus(sessionId: string, sessionExists = true) {
  return useQuery({
    queryKey: QUERY_KEYS.sessionStatus(sessionId),
    queryFn: () => apiClient.getSessionStatus(sessionId),
    enabled: !!sessionId && sessionExists,
    refetchInterval: sessionExists ? 5000 : false, // Check status every 5 seconds only if session exists
  });
}

export function useDisconnectSession() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ sessionId, request }: { sessionId: string; request: DisconnectRequest }) =>
      apiClient.disconnectSession(sessionId, request),
    onSuccess: (_, { sessionId }) => {
      // Invalidate and refetch session data
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.sessions });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.session(sessionId) });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.sessionStatus(sessionId) });
    },
    onMutate: async ({ sessionId }) => {
      // Optimistic update - mark session as disconnecting
      await queryClient.cancelQueries({ queryKey: QUERY_KEYS.session(sessionId) });
      
      const previousSession = queryClient.getQueryData<Session>(QUERY_KEYS.session(sessionId));
      
      if (previousSession) {
        queryClient.setQueryData<Session>(QUERY_KEYS.session(sessionId), {
          ...previousSession,
          sessionState: SessionState.Disconnected,
        });
      }
      
      return { previousSession };
    },
    onError: (_, __, context) => {
      // Rollback on error
      if (context?.previousSession) {
        queryClient.setQueryData(QUERY_KEYS.session(__.sessionId), context.previousSession);
      }
    },
  });
}

export function useLogOffSession() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ sessionId, request }: { sessionId: string; request: LogOffRequest }) =>
      apiClient.logOffSession(sessionId, request),
    onSuccess: (_, { sessionId }) => {
      // Invalidate and refetch session data
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.sessions });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.session(sessionId) });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.sessionStatus(sessionId) });
    },
  });
}